import { API_PRICE_LABEL_SYNC_HISTORY } from 'utils/constants'
import Api from './axiosInstance'

// The only filter keys the API understands. Anything else it receives is turned
// into a case-insensitive regex filter on a field of that name, so a stray key
// (`sortBy`, `isEditor`, …) silently returns an empty listing. Whitelisting here
// keeps that impossible for all three read endpoints.
const FILTER_KEYS = [
  'search',
  'barcode',
  'store',
  'empId',
  'name',
  'isSeen',
  'from',
  'to',
  'updatedAt',
] as const

// `extra` carries non-filter params (page/limit) and bypasses the whitelist.
// Empty values are dropped so blanks never reach the server — note `isSeen`
// omitted means "seen and unseen"; `isSeen=false` means unseen only.
const buildQueryString = (
  filters: Record<string, any> = {},
  extra: Record<string, any> = {},
) => {
  const searchParams = new URLSearchParams()

  const append = (key: string, value: any) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value))
    }
  }

  FILTER_KEYS.forEach((key) => append(key, filters[key]))
  Object.entries(extra).forEach(([key, value]) => append(key, value))

  return searchParams.toString()
}

const withQuery = (path: string, queryString: string) =>
  queryString ? `${path}?${queryString}` : path

// Paginated listing. Sorting is fixed server-side (createdAt descending), so
// there is no sort param to pass.
export const fetchPriceLabelSyncHistory = (
  filters: Record<string, any>,
  pagination: { page: number | string; limit: number | string },
) => {
  return Api.get({
    url: withQuery(
      API_PRICE_LABEL_SYNC_HISTORY.SYNC_HISTORY,
      buildQueryString(filters, pagination),
    ),
  })
}

// Bulk "mark as read" — flips the given (unseen) entries to isSeen: true.
// Requires edit access to the page; a view-only user gets a 401.
export const markPriceLabelSyncHistorySeen = (ids: string[]) => {
  return Api.patch({
    url: API_PRICE_LABEL_SYNC_HISTORY.MARK_SEEN,
    data: { ids },
  })
}

// Unseen count only (no rows) for the sidebar badge — responds with
// `{ data: { unseenCount } }`. Called with no filters, so the badge is a global
// all-stores count; `isSeen` is forced to false server-side either way.
export const fetchPriceLabelSyncHistoryUnseenCount = (
  filters: Record<string, any> = {},
) => {
  return Api.get({
    url: withQuery(
      API_PRICE_LABEL_SYNC_HISTORY.UNSEEN_COUNT,
      buildQueryString(filters),
    ),
  })
}

// Queues a CSV export of the filtered rows — asynchronous. A 200 only means the
// job was accepted: a worker mails the requesting user a download link and logs
// the run on the Export Logs screen under module PRICE_LABEL_SYNC_HISTORY.
// Responds 422 with `errors.msg.runningService` when an export is already
// in flight (one at a time, across all users).
export const exportPriceLabelSyncHistory = (
  filters: Record<string, any> = {},
) => {
  return Api.get({
    url: withQuery(
      API_PRICE_LABEL_SYNC_HISTORY.EXPORT,
      buildQueryString(filters),
    ),
  })
}
